// PlansView.tsx — Plans mode layout compositor
// Layout mirrors ElevationsView: PlansSidebar | Viewport (2D/3D toggle) | PlansPropsPanel

import { useState, useCallback, useEffect } from 'react';
import { useStore } from '../store/useStore';
import PlansSidebar from './PlansSidebar';
import PlansCanvas2D from './PlansCanvas2D';
import PlansViewport3D from './PlansViewport3D';
import PlansPropsPanel from './PlansPropsPanel';
import ResizeHandle from './ResizeHandle';
import type { PlansData } from '../types/panelvar';
import { defaultPlansData } from '../types/panelvar';

export default function PlansView() {
  const project = useStore(s => s.project);
  const ui = useStore(s => s.ui);
  const updateProject = useStore(s => s.updateProject);
  const toggleSidebar = useStore(s => s.toggleSidebar);
  const toggleProps = useStore(s => s.toggleProps);

  const [activeTab, setActiveTab] = useState(ui.buildingTab || 'overall');
  const [sidebarW, setSidebarW] = useState(() => Math.round(window.innerWidth * 0.2));
  const [propsW, setPropsW] = useState(() => Math.round(window.innerWidth * 0.2));

  // Sync activeTab when store buildingTab changes (e.g. sample project load)
  useEffect(() => {
    if (ui.buildingTab && ui.buildingTab !== activeTab) {
      setActiveTab(ui.buildingTab);
    }
  }, [ui.buildingTab]);

  const onSidebarResize = useCallback((dx: number) => {
    setSidebarW(w => Math.max(140, Math.min(400, w + dx)));
  }, []);
  const onPropsResize = useCallback((dx: number) => {
    setPropsW(w => Math.max(140, Math.min(400, w - dx)));
  }, []);

  if (!project) return null;

  const plans = project.plans ?? null;

  function handleSelectTab(tab: string) {
    setActiveTab(tab);
  }

  function handleUpdatePlans(patch: Partial<PlansData>) {
    const current = project!.plans ?? defaultPlansData();
    updateProject({ plans: { ...current, ...patch } });
  }

  return (
    <div className="site-view">
      <aside
        className={`sidebar${ui.sidebarOpen ? '' : ' collapsed'}`}
        style={ui.sidebarOpen ? { width: sidebarW, minWidth: sidebarW } : undefined}
      >
        <PlansSidebar
          plans={plans}
          activeTab={activeTab}
          onSelectTab={handleSelectTab}
          collapsed={!ui.sidebarOpen}
          onToggleCollapse={toggleSidebar}
        />
      </aside>

      {ui.sidebarOpen && <ResizeHandle onResize={onSidebarResize} />}

      <div className="site-view-viewport">
        {ui.buildingViewMode === '2d' ? (
          <PlansCanvas2D plans={plans} activeTab={activeTab} elevations={project.elevations} panels={project.panels} />
        ) : (
          <PlansViewport3D plans={plans} activeTab={activeTab} elevations={project.elevations} panels={project.panels} />
        )}
      </div>

      {ui.propsOpen && <ResizeHandle onResize={onPropsResize} />}

      <aside
        className={`props-panel${ui.propsOpen ? '' : ' collapsed'}`}
        style={ui.propsOpen ? { width: propsW, minWidth: propsW } : undefined}
      >
        <PlansPropsPanel
          plans={plans}
          activeTab={activeTab}
          onUpdatePlans={handleUpdatePlans}
          elevations={project.elevations}
          panels={project.panels}
          collapsed={!ui.propsOpen}
          onToggleCollapse={toggleProps}
        />
      </aside>
    </div>
  );
}
